import React, { Component } from "react";
import { Theme, withStyles } from "@material-ui/core";
import { WithTranslation, withTranslation } from "react-i18next";
import createStyles from "@material-ui/core/styles/createStyles";
import classNames from "classnames";
import ActiveIcon from "@material-ui/icons/Check";
import InactiveIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import Fab from "@material-ui/core/Fab";
import { RouteComponentProps } from "react-router";
import List, { Header } from "../List";
import AdminStore, { Admin } from "../../stores/adminStore";
import { inject, observer } from "mobx-react";
import { COLORS } from "../../styles/colors";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Filters from "../Filters";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import AppStore from "../../stores/appStore";

interface MatchParams {}

interface AdminsListProps extends WithTranslation {}

interface InjectedProps
  extends AdminsListProps,
    RouteComponentProps<MatchParams> {
  adminStore: AdminStore;
  appStore: AppStore;
  classes: any;
}

interface AdminListState {
  filters: {
    [key: string]: string;
  };
  sortParams: {
    [key: string]: string;
  };
}

@inject("adminStore", "appStore")
@observer
class AdminsList extends Component<AdminsListProps, AdminListState> {
  get p() {
    return this.props as InjectedProps;
  }

  state = {
    filters: {},
    sortParams: {}
  };

  filtersList = [
    {
      name: "lastName",
      label: this.p.t("ADMIN_LIST.SURNAME"),
      type: "input"
    },
    {
      name: "email",
      label: this.p.t("ADMIN_LIST.EMAIL"),
      type: "input"
    },
    {
      name: "phone",
      label: this.p.t("ADMIN_LIST.PHONE_NUMBER"),
      type: "input"
    },
    {
      name: "active",
      label: this.p.t("ADMIN_LIST.STATUS"),
      type: "select",
      options: [
        { label: this.p.t("ADMIN_LIST.ALL"), value: "all" },
        { label: this.p.t("ADMIN_LIST.ACTIVE"), value: "true" },
        { label: this.p.t("ADMIN_LIST.INACTIVE"), value: "false" }
      ]
    }
  ];

  headers = [
    {
      name: this.p.t("ADMIN_LIST.NAME"),
      key: "firstName"
    },
    {
      name: this.p.t("ADMIN_LIST.SURNAME"),
      key: "lastName",
      sort: true
    },
    {
      name: this.p.t("ADMIN_LIST.EMAIL"),
      key: "email"
    },
    {
      name: this.p.t("ADMIN_LIST.PHONE_NUMBER"),
      key: "phone"
    },
    {
      name: this.p.t("ADMIN_LIST.STATUS"),
      key: "active",
      sort: true
    }
  ];

  getAdminList = (sortParams: any) => {
    if (Object.keys(sortParams).length) {
      this.setState({ sortParams });
    }
    const sort = { ...this.state.sortParams, ...sortParams };
    const params = {
      ...sort,
      ...this.state.filters
    };
    if (params.active === "all") {
      const { active, ...data } = params;
      this.p.adminStore.getAdminListAction(data);
    } else {
      this.p.adminStore.getAdminListAction(params);
    }
  };

  handleAddAdmin = () => {
    this.p.history.push(`/admins/add`);
  };

  handleOpenAdmin = (admin: Admin) => () => {
    this.p.history.push(`/admins/details/${admin.id}`);
  };

  handleChange = (filters: any) => {
    this.setState({ filters }, () => this.getAdminList({}));
  };

  render() {
    const {
      classes,
      t,
      adminStore: { adminList, adminsTotalCount },
      appStore: { isFilter }
    } = this.p;
    return (
      <div className={classes.listContainer}>
        <Typography
          className={classNames(classes.dividerFullWidth, "title")}
          display="block"
          variant="subtitle1"
        >
          {t("ADMIN_LIST.TITLE")}
        </Typography>
        <Divider component="div" className={"divider"} />
        <Filters handleFilter={this.handleChange} filters={this.filtersList} />
        <List
          data={adminList}
          getData={this.getAdminList}
          headers={this.headers}
          isFilter={isFilter}
          totalCount={adminsTotalCount}
          headerClass={classes.headerPadding}
        >
          {adminList &&
            adminList.length &&
            adminList.map((item: any, i: number) => (
              <TableRow
                key={item.uuid || item.id || i}
                className={"rowList"}
                onClick={this.handleOpenAdmin(item)}
              >
                {this.headers.map((header: Header) => (
                  <TableCell key={header.key} className={classes.tableCell}>
                    {header.key === "active" ? (
                      <>
                        {!item[header.key] ? (
                          <InactiveIcon className={classes.inactiveColor} />
                        ) : (
                          <ActiveIcon className={classes.activeColor} />
                        )}
                      </>
                    ) : (
                      <>{item[header.key]}</>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </List>
        <Tooltip title="Add" placement="top-start">
          <Fab
            color="primary"
            aria-label="Add"
            className={classNames(classes.fab, classes.addButton)}
            onClick={this.handleAddAdmin}
            data-cy="addAdmin"
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      </div>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    listContainer: {
      maxWidth: "100%",
      position: "relative",
      height: "calc(100vh - 90px)"
    },
    addButton: {
      color: "#fff",
      position: "fixed",
      backgroundColor: COLORS.PRIMARY_DARK,
      bottom: 20,
      right: 40
    },

    inactiveColor: {
      color: COLORS.ERROR
    },

    activeColor: {
      color: COLORS.SUCCESS
    },

    headerPadding: {
      padding: "10px 20px 10px 10px;",
      textAlign: "left"
    }
  });

export default withStyles(styles)(withTranslation()(AdminsList));
